import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { SuccessData } from 'types/api';
import { User } from './types';
import { pick, omitWithValues } from 'utils/object';
import { uploadMutations } from '../uploads';

interface CreateUserParams {
  team: string;
  email: string;
  name: string;
  office: string;
  type: string;
  language: string;
}

// increment later on
type UpdateUserParams = { id: string } & Pick<User, 'language' | 'office' | 'name' | 'type'>;

type Language = Pick<User, 'language'>;

export const userMutations = getMutations({
  toggleUser: {
    action: async ({ id, disabled }: { id: string; disabled: boolean }): Promise<SuccessData> => {
      const route = disabled ? 'disable' : 'enable';

      return executeApiCall({
        path: `/users/${route}`,

        payload: {
          id,
        },
      });
    },

    messages: {
      error: 'error-while-updating-user',
    },
  },

  createUser: {
    action: async (payload: CreateUserParams): Promise<User> => {
      return executeApiCall({
        path: '/users',

        payload: pick(payload, ['name', 'email', 'office', 'type', 'language', 'team']),
      });
    },

    validate: ({ name, email, office, type, language }: CreateUserParams) => {
      const ok = [name, email, office, type, language].every(Boolean);

      return {
        ok,
        message: 'make-sure-everything-is-filled-correctly',
      };
    },
  },

  updateUser: {
    action: async (payload: UpdateUserParams): Promise<User> => {
      const possibleValues = pick(payload, ['name', 'office', 'type', 'language', 'id']);

      return executeApiCall({
        path: '/users',

        method: 'put',

        payload: omitWithValues(possibleValues, ['']),
      });
    },

    validate: ({ name, office, type, language }: UpdateUserParams) => {
      const ok = [name, office, type, language].every(
        (value) => typeof value === 'string' && value,
      );

      return {
        ok,
        message: 'make-sure-everything-is-filled-correctly',
      };
    },
  },

  syncSampleId: {
    action: async (payload: { id: string }): Promise<{ sampleId: string }> => {
      return executeApiCall({
        path: '/users/sync-sample-id',

        payload,
      });
    },
  },

  changeUserTeam: {
    action: async (payload: { id: string; team: string }): Promise<{ sampleId: string }> => {
      return executeApiCall({
        path: '/users/team',

        method: 'put',

        payload,
      });
    },

    messages: {
      error: 'error-while-changing-team',
    },
  },

  toggleSecondaryTeam: {
    action: async (payload: {
      userId: string;
      teamId: string;
    }): Promise<Pick<User, 'secondaryTeams'>> => {
      return executeApiCall({
        path: '/users/toggle-secondary-team',

        payload,
      });
    },
  },

  changeOwnLanguage: {
    action: async (payload: Language): Promise<Language> => {
      await executeApiCall({
        path: '/users/language',

        payload,
      });

      return {
        language: payload.language,
      };
    },
  },

  changeOwnPicture: {
    action: async ({
      file,
      userId,
      base64,
    }: {
      file: File;
      base64?: string | null;
      userId: string;
    }): Promise<{ url: string }> => {
      const { url } = await uploadMutations.uploadFile.action({
        file,
        base64,
        prefix: 'users',
        type: 'PUBLIC',
        enforcedName: userId,
      });

      await executeApiCall({
        path: '/users/picture',

        payload: {
          picture: url,
        },
      });

      return {
        url,
      };
    },
  },
});
